<template>
  <div class="ioc-trail-table box flex column" :id="tableNameId">
    <div class="box flex gaps">
      <div class="box center table-menu-title"><h5>Progressions</h5></div>
      <div class="box grow flex justify-flex-end table-menu-options">
        <el-tooltip class="item" effect="dark" content="Sort By" placement="top" :enterable="false">
          <span class="body2">
            <el-select
                v-model="sortSel"
                @change="sortTrailList(sortSel)"
                placeholder="Select"
                class="body2"
                style="max-width: 125px"
            >
              <el-option v-for="item in sortOptions" :key="item.value" :label="item.label" :value="item.value">
                <span class="body2">{{ item.label }}</span>
              </el-option>
            </el-select>
          </span>
        </el-tooltip>
        <el-tooltip class="item" effect="dark" content="Collapse/Expand" placement="top" :enterable="false">
          <span class="body2">
            <span
                class="body2 menu-toggle cursor-pointer"
                v-if="trail.collapsedView"
                @click="collapseView(!trail.collapsedView)"
            >
              <i class="display-inline-block lh-40 material-icons">unfold_more</i>
            </span>
            <span class="body2 menu-toggle cursor-pointer" v-else @click="collapseView(!trail.collapsedView)">
              <i class="display-inline-block lh-40 material-icons">unfold_less</i>
            </span>
          </span>
        </el-tooltip>
        <el-tooltip class="item" effect="dark" content="Toggle Details" placement="top" :enterable="false">
          <span class="body2 menu-toggle cursor-pointer" @click="hideIocDetailsFn()">
            <i class="display-inline-block lh-40 material-icons">apps</i>
          </span>
        </el-tooltip>
        <el-tooltip class="item" effect="dark" content="Legend" placement="top" :enterable="false">
          <span class="body2 menu-toggle cursor-pointer" @click="openLegend = !openLegend">
            <i class="display-inline-block lh-40 material-icons">assistant</i>
          </span>
        </el-tooltip>
      </div>
    </div>
    <el-table
        id="ioc-timeline-table"
        class="box grow"
        ref="iocTable"
        highlight-current-row
        :span-method="hostColSpanMethod"
        :data="iocs"
        :height="tableHeight"
        :header-cell-class-name="tableHeaderClassName"
        :row-class-name="tableRowClassName"
        :cell-class-name="tableCellClassName"
        :row-style="{ 'max-height': '40px' }"
        style="width: 100%"
    >
      <el-table-column
          v-for="(col, idx) in columns"
          :key="`${col.name}-${idx}`"
          :type="col.datatype"
          :prop="col.name"
          :label="col.label"
          :sortable="col.sortable"
          :width="380"
          :min-width="col['min-width']"
          :row-key="currentRow"
          :fixed="col.fixed"
      >
        <!-- custom headers  -->
        <template v-slot:header="scope">
          <div class="flex column tooltip-wrapper">
            <el-tooltip
                class="item"
                effect="dark"
                placement="top"
                :enterable="false"
            >
              <div slot="content" v-html="getHeaderHostContainerData(col)"/>
              <span class="host-box-wrapper box flex grow" @click.stop.prevent="handleHostClick(col)">
               <span class="box host-box">
                 <i class="display-block icon icon-criticality"
                    :class="`icon-criticality-${getHeaderCriticality(scope.column)}`"/>
                 <span class="lh-20 mr-5 fab" :class="getHeaderIcon(scope.column)"></span>
                 <div class="host-name">{{ getHeaderLabel(scope.column) }}</div>
               </span>
              </span>
            </el-tooltip>
            <el-tooltip
                class="item"
                effect="dark"
                :content="getHeaderHostContainerLabel(col)"
                placement="bottom"
                :enterable="false"
            >
              <div class="host-box-wrapper child-host-header" v-if="col.parentHeader">
                <i class="display-block icon-subdirectory-arrow"/>
                <div class="box host-box">
                  <div class="lh-20 mr-5 fab" :class="getHeaderIcon(col)"></div>
                  <div class="display-block body1 host-label" v-html="getHeaderHostContainerLabel(col)"/>
                </div>
              </div>
            </el-tooltip>
          </div>
        </template>
        <template v-slot="scope">
          <div
              v-if="col.datatype === 'iocpoint' && col.display !== 'hostTarget'"
              :class="`ioc-summary cursor-pointer ${getInfluencedByClass(scope.row, col)}`"
          >
            <span
                class="ioc-summary_details box flex"
                :class="{ 'details-hidden': hideIocDetails, stacked: scope.row.occurrence > 1 }"
                @click.stop.prevent="handleRowClick(scope.row, {}, $event)"
                @mouseover.stop.prevent="hideIocDetails ? showIocDetails(scope.row, $event, 'details-hidden') : false"
                @mouseleave.stop.prevent="hideIocDetails ? removeIocDetails(scope.row, $event, 'details-hidden') : false"
            >
              <span
                  class="ioc-point display-inline-block"
                  :class="[{ 'trail-head': scope.$index === 0 }, { 'trail-end': scope.$index === iocs.length - 1 }]"
              >
                <i class="tactic-icon" :class="getIocIconClass(scope.row)"/>
              </span>
              <div class="box flex column flex-1 ioc-details">
                <p class="box body1 display-inline" v-if="scope.row.rule">{{ scope.row.rule.name }}</p>
                <p class="box body1 display-inline" v-else>{{ scope.row.iocSummary }}</p>
                <span class="box ioc-details_time" v-if="scope.row.occurrence > 1 && sortSel === 'ascending'">
                  <time class="display-inline-block caption1"
                  >{{ $moment(scope.row.seenTime).format('MMM Do,h:mm a') }} -</time
                  >
                  <time class="display-inline-block caption1">{{
                      $moment(scope.row.occurrenceEndTime).format('MMM Do,h:mm a')
                    }}</time>
                </span>
                <span class="box ioc-details_time" v-else-if="scope.row.occurrence > 1 && sortSel !== 'ascending'">
                  <time class="display-inline-block caption1"
                  >{{ $moment(scope.row.occurrenceEndTime).format('MMM Do,h:mm a') }} -</time
                  >
                  <time class="display-inline-block caption1">{{
                      $moment(scope.row.seenTime).format('MMM Do,h:mm a')
                    }}</time>
                </span>
                <span class="box ioc-details_time" v-else>
                  <time class="display-inline-block caption1">{{
                      $moment(scope.row.seenTime).format('MMM Do,h:mm a')
                    }}</time>
                </span>
              </div>
              <span class="box display-inline-block">
                <severity-tag
                    :classList="'button2 capitalize-first-letter'"
                    :severityLevel="scope.row.detectionSeverity">
                  </severity-tag>
              </span>
              <span class="ioc-occurrence body1" v-show="scope.row.occurrence > 1">
                <p :class="{ body1: scope.row.occurrence < 100, caption1: scope.row.occurrence > 100 }">
                  {{ scope.row.occurrence }}
                </p>
                <p></p>
              </span>
            </span>
          </div>
          <div
              v-else-if="col.display === 'hostTarget' && idx === scope.row.agentColIndex"
              :class="`ioc-summary cursor-pointer ${getInfluencedByClass(scope.row, col)}`"
          >
            <span
                class="ioc-summary_details box flex"
                :class="{ 'details-hidden': hideIocDetails, stacked: scope.row.occurrence > 1 }"
                v-bind:style="[iocColorFlag ? {'backgroundColor': '#F6EED8'} : {} ]"
                @click.stop.prevent="handleRowClick(scope.row, {}, $event)"
                @mouseover.stop.prevent="hideIocDetails ? showIocDetails(scope.row, $event, 'details-hidden') : false"
                @mouseleave.stop.prevent="hideIocDetails ? removeIocDetails(scope.row, $event, 'details-hidden') : false"
            >
              <span
                  class="box ioc-point display-inline-block"
                  :class="[{ 'trail-head': scope.$index === 0 }, { 'trail-end': scope.$index === iocs.length - 1 }]"
              >
                <i class="tactic-icon" :class="getIocIconClass(scope.row)"/>
              </span>
            <div class="box flex column flex-1 ioc-details">
              <p class="box body1 display-inline" v-if="scope.row.rule">{{ scope.row.rule.name }}</p>
              <p class="box body1 display-inline" v-else>{{ scope.row.iocSummary }}</p>
              <span class="box ioc-details_time" v-if="scope.row.occurrence > 1 && sortSel === 'ascending'">
                  <time class="display-inline-block caption1"
                  >{{ $moment(scope.row.seenTime).format('MMM Do,h:mm a') }} -</time
                  >
                  <time class="display-inline-block caption1">{{
                      $moment(scope.row.occurrenceEndTime).format('MMM Do,h:mm a')
                    }}</time>
                </span>
              <span class="box ioc-details_time" v-else-if="scope.row.occurrence > 1 && sortSel !== 'ascending'">
                  <time class="display-inline-block caption1"
                  >{{ $moment(scope.row.occurrenceEndTime).format('MMM Do,h:mm a') }} -</time
                  >
                  <time class="display-inline-block caption1">{{
                      $moment(scope.row.seenTime).format('MMM Do,h:mm a')
                    }}</time>
                </span>
              <span class="box ioc-details_time" v-else>
                  <time class="display-inline-block caption1">{{
                      $moment(scope.row.seenTime).format('MMM Do,h:mm a')
                    }}</time>
                </span>
            </div>
            <span class="box display-inline-block flex center">
                <severity-tag
                    :classList="'button2 capitalize-first-letter'"
                    :severityLevel="scope.row.detectionSeverity">
                  </severity-tag>
              </span>
            <span class="ioc-occurrence body1" v-show="scope.row.occurrence > 1">
                <p :class="{ body1: scope.row.occurrence < 100, caption1: scope.row.occurrence > 100 }">
                  {{ scope.row.occurrence }}
                </p>
              </span>
            </span>
          </div>
          <div
              v-else-if="
              scope.row.lateralmove &&
                scope.row.sourceAgentColIndex === scope.row.targetAgentColIndex &&
                scope.row.sourceAgentColIndex === idx
            "
              class="local-host-target cursor-pointer"
              @click.stop.prevent="handleRowClick(scope.row, {}, $event)"
          >
            <ioc-bezier-curve
                :table-id="tableNameId"
                :row="scope.row"
                :start="sortSel === 'ascending' ? 'left' : 'right'"
                :render-watch="hostCount"
                :direction="sortSel === 'ascending' ? 'ltr' : 'rtl'"
                :right-offset="idx === 0 && sortSel === 'ascending' ? 0 : 0"
            ></ioc-bezier-curve>
          </div>
          <div
              v-else-if="
              scope.row.lateralmove &&
                scope.row.sourceAgentColIndex < scope.row.targetAgentColIndex &&
                scope.row.sourceAgentColIndex === idx
            "
              class="right-start cursor-pointer"
              @click.stop.prevent="handleRowClick(scope.row, {}, $event)"
          >
            <ioc-bezier-curve
                :table-id="tableNameId"
                :row="scope.row"
                :start="sortSel === 'ascending' ? 'left' : 'right'"
                :render-watch="hostCount"
                :direction="sortSel === 'ascending' ? 'ltr' : 'rtl'"
                :right-offset="idx === 0 && sortSel === 'ascending' ? 0 : 0"
            ></ioc-bezier-curve>
          </div>
          <div
              v-else-if="
              scope.row.lateralmove &&
                scope.row.sourceAgentColIndex > scope.row.targetAgentColIndex &&
                scope.row.sourceAgentColIndex === idx
            "
              class="left-start cursor-pointer"
              @click.stop.prevent="handleRowClick(scope.row, {}, $event)"
          >
            <ioc-bezier-curve
                :table-id="tableNameId"
                :row="scope.row"
                :start="sortSel === 'ascending' ? 'right' : 'left'"
                :direction="sortSel === 'ascending' ? 'ltr' : 'rtl'"
                :render-watch="hostCount"
                :right-offset="idx > 0 && sortSel === 'ascending' ? -5 : 0"
                :left-offset="idx > 0 && sortSel === 'ascending' ? 5 : 0"
            ></ioc-bezier-curve>
          </div>
          <div v-else-if="col.datatype === 'boolean'">
            <span>{{
                scope.row[col.name] ? `${scope.row[col['origin-property']]} => ${scope.row[col['dest-property']]}` : ' '
              }}</span>
          </div>
          <div v-else-if="col.datatype === 'datetime'" :class="`${getInfluencedByClass(scope.row, col)}`">
            <time class="subtitle1">{{ getFormatedDateTime(scope.row, col) }}</time>
          </div>
          <div v-else-if="col.datatype === 'number'" :class="`${getInfluencedByClass(scope.row, col)}`">
            <span class="body1">{{ scope.row[col.name] }}</span>
          </div>
          <div v-else :class="`${getInfluencedByClass(scope.row, col)}`">
            <span class="body1">{{
                `${col.prefix || ''} ${$_.isUndefined(scope.row[col.name]) ? '' : scope.row[col.name]}`
              }}</span>
          </div>
        </template>
      </el-table-column>
      <infinite-loading
          slot="append"
          @infinite="loadDetectionHandler"
          force-use-infinite-wrapper=".el-table__body-wrapper"
      >
        <div slot="no-more"></div>
        <div slot="no-results"></div>
      </infinite-loading>
    </el-table>
    <el-dialog :visible.sync="openLegend" :top="`5vh`">
      <img v-once src="/static/images/attack_progression_helper.png" id="progression_legend" alt="progression_legend"/>
    </el-dialog>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {OrderByEnum} from '@/constants/orderby.constants'
import {getContainerNameLabel} from '@/utilities/agent.helper'
import IocBezierCurve from '@/components/curve/IocBezierCurve.vue'
import SeverityTag from '@/components/button/SeverityTag.vue'
import {TRAIL_DETAILS_USER_PREF} from '@/store/modules/trails/trailsinfo.type'

export default {
  name: 'IocTrailTableElement',
  props: {
    tableId: {type: String},
    tableSize: {type: String, default: ''},
    tableHeight: {type: [String, Number], default: 400},
    hostCount: {type: [Number], default: 0},
    showHostLabel: {type: [Boolean], default: true},
    trail: {
      type: Object,
      default: () => {
        return {}
      }
    },
    onSelectionChange: {type: [Function, Boolean], default: false},
    onRowClick: {type: [Function, Boolean], default: false},
    iocColorFlag: {type: [Boolean], default: false},
    onHostClick: {
      type: [Function],
      default: () => {
      }
    }
  },
  data() {
    return {
      tableNameId: `table-${this.tableId.replace(/[#:.]/g, '-')}`,
      page: 0,
      recordCount: 0, // store detections count
      currentRow: undefined,
      columns: [],
      iocs: [],
      sortSel: this.trail.orderByDir,
      sortOptions: [
        {
          value: OrderByEnum.DESC,
          label: 'Newest First'
        },
        {
          value: OrderByEnum.ASC,
          label: 'Oldest First'
        }
      ],
      hideIocDetails: false,
      openLegend: false,
      isDown: false,
      startX: '',
      scrollLeft: ''
    }
  },
  methods: {
    collapseView(isCollapse) {
      this.resetRecords()
      this.trail.setCollapsedView(isCollapse)
      this.updateTableData(this.page)
      this.trail.setDetectionsPage(++this.page)
      this.scrollTop()
      this.$store.dispatch(TRAIL_DETAILS_USER_PREF, {
        orderBy: this.sortSel,
        collapsedView: isCollapse
      })
    },
    getIocIconClass(ioc) {
      if (ioc.iocTactic) {
        return `icon-detections-${ioc.iocTactic.toLowerCase().replace(' ', '_')}`
      }
      return 'icon-detections-default'
    },
    hideIocDetailsFn() {
      this.hideIocDetails = !this.hideIocDetails
    },
    showIocDetails(row, event) {
      if (event.target.classList.contains('details-hidden')) {
        event.target.classList.remove('details-hidden')
      }
      if (event.target.parentElement.classList.contains('details-hidden')) {
        event.target.parentElement.classList.remove('details-hidden')
      }
    },
    removeIocDetails(row, event) {
      if (event.target.classList.contains('ioc-summary_details')) {
        event.target.classList.add('details-hidden')
      } else {
        event.target.parentElement.classList.add('details-hidden')
      }
    },
    resetRecords() {
      this.iocs.length = 0
      this.page = 0
    },
    sortTrailList(sortBy) {
      this.sortChange({column: {}, order: sortBy})
    },
    headerSortChange(column, elm) {
      if (!elm) {
        return false
      }
      if (column.sortable) {
        if (column.order === OrderByEnum.ASC) {
          elm.classList.remove(OrderByEnum.DESC)
          elm.classList.add(OrderByEnum.ASC)
        } else if (column.order === OrderByEnum.DESC) {
          elm.classList.remove(OrderByEnum.ASC)
          elm.classList.add(OrderByEnum.DESC)
        } else {
          elm.classList.remove(OrderByEnum.DESC)
          elm.classList.remove(OrderByEnum.ASC)
        }
      }
    },
    sortChange({column, prop, order}) {
      if (column) {
        this.headerSortChange(column, this.$el.querySelector(`.el-table__fixed .${column.id}`))
      } else {
        let sortCols = this.$el.querySelectorAll('th.is-sortable')
        for (let i = 0; i < sortCols.length; i++) {
          sortCols[i].classList.remove(OrderByEnum.DESC)
          sortCols[i].classList.remove(OrderByEnum.ASC)
        }
      }
      // sort table by column, reset page to top and update table
      // if column is not defined, fall back to default sort
      if (_.isUndefined(column) || _.isNull(column)) {
        this.trail.prepareIoCAttackDetails()
      } else {
        this.trail.prepareIoCAttackDetails([], [], [], order)
      }
      this.sortSel = order
      this.resetRecords()
      this.updateTableData(this.page)
      this.trail.setDetectionsPage(++this.page)
      this.scrollTop()
      this.$store.dispatch(TRAIL_DETAILS_USER_PREF, {
        orderBy: this.sortSel,
        collapsedView: this.trail.collapsedView
      })
    },
    scrollTop() {
      // scroll to the top
      let tablerows = this.$el.querySelectorAll('tr.el-table__row')
      tablerows[0].scrollIntoView({
        behavoir: 'smooth',
        block: 'nearest',
        inline: 'start'
      })
    },
    getHostDetails(col) {
      let agentInfo
      if (col.name === 'host') {
        let headerColLabel = 'label'
        if (!_.isEmpty(col.parentHeader)) {
          headerColLabel = 'parentHeader'
        }
        agentInfo = _.find(this.agentList.models, agent => {
          return col[headerColLabel] === agent.agentId.split('.')[1]
        })
        if (agentInfo) {
          return agentInfo
        }
      }
      return undefined
    },
    getContainerDetails(col) {
      let containerInfo

      if (col.name === 'host') {
        let containerLabel = 'label'
        containerInfo = _.find(this.containerListInfo.models, container => {
          return container.containerId.startsWith(col[containerLabel])
        })
        if (containerInfo) {
          let details = containerInfo.trailblazerContainerMetadata || containerInfo.containerMetadata
          return getContainerNameLabel(details.containerNames[0])
        }
      }
      return undefined
    },
    loadDetectionHandler($state) {
      // called on component load
      if (_.isEmpty(this.iocs)) {
        $state.reset()
      }
      if (_.isEmpty(this.trail.virtualState)) {
        this.trail.virtualState = $state
        // fake empty row added to create vertical column lines
        this.iocs.splice(0, 0, {})
      }
      if (this.trail.hasMoreDetections(this.page)) {
        this.iocs = this.iocs.concat(_.cloneDeep(this.trail.getNextDetectionsPage(this.page)))
        this.trail.setDetectionsPage(++this.page)
        $state.loaded()
      } else {
        $state.complete()
      }
    },
    doRefreshData() {
      /*
        Refresh under the following conditions
        1. New detection(s)
        2. AND desc sort or asc sort and user is on last page
      */
      let progression = false
      // check if a progression happened (lateral movement)
      if (this.hostCount !== this.columns.length) {
        for (let idx = 0; idx < this.trail.hostPath.length; idx++) {
          this.$set(this.columns, idx, this.trail.hostPath[idx])
        }
        progression = true
      }
      // check if new detection(s) occurred
      if (this.trail.collapsedView) {
        this.refreshCollapsedView(progression)
      } else {
        this.refreshExpandView(progression)
      }
    },
    refreshExpandView(progression) {
      let detectionsCount = this.trail.getDetectionsCount(); // store the result to avoid multiple calls
      // check if new detection(s) occurred
      if (this.recordCount !== detectionsCount) {
        let iocCntDiff = detectionsCount - this.recordCount;
        if (this.sortSel === OrderByEnum.DESC) {
          // start index should be 1 (0 is a fake row)
          let startIndex = 1;
          let newIocs = this.trail.trailIocList.slice(0, iocCntDiff); // create a new array of new detections
          this.iocs = newIocs.concat(this.iocs.slice(startIndex)); // concatenate new detections with existing ones

          // update source and target host index for visible/rendered iocs
          if (progression) {
            for (let idx = iocCntDiff; idx < this.iocs.length; idx++) {
              _.merge(this.iocs[idx + startIndex], this.trail.trailIocList[idx]);
            }
          }
        } else if (!this.trail.hasMoreDetections(this.page)) {
          let newIocs = this.trail.trailIocList.slice(this.recordCount, detectionsCount); //  create a new array of new detections
          this.iocs = this.iocs.concat(newIocs); // concatenate new detections with existing ones
        }
        this.recordCount = detectionsCount;
        this.refreshLayout();
      }
    },
    refreshCollapsedView(progression) {
      const detectionsCount = this.trail.getDetectionsCount();
      const collapsedDetectionsCount = this.trail.getCollapsedDetectionsCount();
      const collapsedTrailIocList = this.trail.collapsedTrailIocList;

      if (this.recordCount !== detectionsCount) {
        let localRecordCount = this.iocs.length - 1;
        let iocCntDiff = collapsedDetectionsCount - localRecordCount;

        if (this.sortSel === OrderByEnum.DESC) {
          let startIndex = 1;
          if (iocCntDiff === 0) {
            _.merge(this.iocs[startIndex], collapsedTrailIocList[0]);
          } else {
            for (let idx = 0; idx < iocCntDiff; idx++) {
              this.iocs.splice(idx + startIndex, 0, collapsedTrailIocList[idx]);
            }
            if (progression) {
              for (let idx = iocCntDiff; idx < this.iocs.length; idx++) {
                _.merge(this.iocs[idx + startIndex], collapsedTrailIocList[idx]);
              }
            }
          }
        } else if (!this.trail.hasMoreDetections(this.page)) {
          if (iocCntDiff === 0) {
            _.merge(this.iocs[localRecordCount], collapsedTrailIocList[localRecordCount - 1]);
          }
          for (let idx = localRecordCount; idx < collapsedDetectionsCount; idx++) {
            this.iocs.push(collapsedTrailIocList[idx]);
          }
        }
        this.recordCount = detectionsCount;
        this.refreshLayout();
      }
    },
    updateTableData(page) {
      // set host columns appropriately
      for (let idx = 0; idx < this.trail.hostPath.length; idx++) {
        this.$set(this.columns, idx, this.trail.hostPath[idx])
      }
      for (let idx = 0; idx <= page; idx++) {
        this.iocs = this.iocs.concat(_.cloneDeep(this.trail.getNextDetectionsPage(idx)))
      }
      // fake empty row added to create vertical column lines
      this.iocs.splice(0, 0, {})
      if (this.trail.virtualState && this.page === 0) {
        this.trail.virtualState.reset()
      }
      this.refreshLayout()
    },
    hostColSpanMethod({row, column, rowIndex, columnIndex}) {
      if (column.property === 'host') {
        if (column.label === row.agentIdLabel) {
          return {
            rowspan: 1,
            colspan: this.hostCount ? this.hostCount - row.agentColIndex : 1
          }
        } else if (row.lateralmove) {
          // if left to right lateralmovement, set colpan before and after movement to 1
          if (
            row.targetAgentColIndex > row.sourceAgentColIndex &&
              (columnIndex < row.sourceAgentColIndex || columnIndex > row.sourceAgentColIndex)
          ) {
            return {
              rowspan: 1,
              colspan: 1
            }
          }
          // if right to left lateralmovement, need to create room (set colspan = 0) for movment to work backwards
          // 1. columns from where the target starts to before source column are cleared
          else if (
            row.targetAgentColIndex < row.sourceAgentColIndex &&
              columnIndex >= row.targetAgentColIndex &&
              columnIndex < row.sourceAgentColIndex
          ) {
            return {
              rowspan: 0,
              colspan: 0
            }
          }
          // 2. any column before the target, keep the space and set colspan = 1
          else if (row.targetAgentColIndex < row.sourceAgentColIndex && columnIndex < row.targetAgentColIndex) {
            return {
              rowspan: 1,
              colspan: 1
            }
          }

          return {
            rowspan: 1,
            colspan: Math.abs(row.targetAgentColIndex - row.sourceAgentColIndex) + 1
          }
        } else if (columnIndex < row.agentColIndex) {
          return {
            rowspan: 1,
            colspan: 1
          }
        } else {
          // Added a dummy row to handle grid border
          if (_.isEmpty(row)) {
            return {
              rowspan: 1,
              colspan: 1
            }
          }
          return {
            rowspan: 0,
            colspan: 0
          }
        }
      }
    },
    getHeaderLabel(col) {
      if (col.property !== 'host') {
        return col.label
      }
      let hostCol = _.find(this.trail.hostPath, {label: col.label})
      if (hostCol.altLabel) {
        return hostCol.altLabel
      }
      return col.label
    },
    getHeaderIcon(col) {
      let hostCol = _.find(this.trail.hostPath, {label: col.label})
      if (hostCol.icon) {
        return hostCol.icon
      }
      return col.parentHeader ? 'icon-docker' : ''
    },
    getHeaderCriticality(col) {
      let hostCol = _.find(this.trail.hostPath, {label: col.label})
      if (hostCol.criticality) {
        return hostCol.criticality.toLowerCase()
      }
      return 'regular'
    },
    getHeaderHostContainerLabel(col) {
      if (_.isEmpty(col.parentHeader)) {
        return ''
      }
      let containerInfo = _.find(this.containerListInfo.models, container => {
        return container.containerId.startsWith(col['label'])
      })
      if (containerInfo) {
        let details = containerInfo.trailblazerContainerMetadata || containerInfo.containerMetadata
        let containerName = getContainerNameLabel(details.containerNames[0])
        return containerName.substring(0, 12)
      }

      return col.label
    },
    getHeaderHostContainerData(col) {
      const containerLabel = this.getHeaderHostContainerLabel(col)
      let hostCol = _.find(this.trail.hostPath, {label: col.label})
      return `
        <div class="host-data">
          <div class="host-data-row">
            <div class="field-label">Name: </div>
            <div class="host-name">${containerLabel !== '' ? containerLabel : hostCol.altLabel}</div>
          </div>
          <div class="host-data-row">
            <div class="field-label">Host type: </div>
            <div class="host-name">${hostCol.os || 'N/A'}</div>
          </div>
           <div class="host-data-row">
            <div class="field-label">Criticality: </div>
            <div class="host-name">${this.getHeaderCriticality(col)}</div>
          </div>
        </div>
`
    },
    tableHeaderClassName(header) {
      if (header.column.property === 'host') {
        let headerClass = `${header.column.property.toLowerCase()}-header body1`
        if (!this.showHostLabel) {
          headerClass += ' header-label_hide'
        }
        return headerClass
      }
      return `${header.column.property.toLowerCase()}-header`
    },
    tableRowClassName({row}) {
      if (row.lateralmove) {
        return 'lateral-move-row'
      }
    },
    tableCellClassName(table) {
      const {row, column, columnIndex} = table;
      const {lateralmove, sourceAgentColIndex, targetAgentColIndex, agentColIndex} = row;

      if (column.property === 'host' && columnIndex === agentColIndex) {
        return 'hit-host-target';
      }

      if (!lateralmove) {
        return `${column.property.toLowerCase()}-col-cell`;
      }

      if (sourceAgentColIndex === targetAgentColIndex && columnIndex === sourceAgentColIndex) {
        return 'local-host-target';
      }

      const isRightMove = sourceAgentColIndex < targetAgentColIndex;
      const isLeftMove = !isRightMove;

      if (isRightMove && columnIndex > sourceAgentColIndex && columnIndex < targetAgentColIndex) {
        return 'right-host-target';
      }

      if (isRightMove && columnIndex === sourceAgentColIndex) {
        return 'right-start-host-target';
      }

      if (isRightMove && columnIndex === targetAgentColIndex) {
        return 'right-end-host-target';
      }

      if (isLeftMove && columnIndex < sourceAgentColIndex && columnIndex > targetAgentColIndex) {
        return 'left-host-target';
      }

      if (isLeftMove && columnIndex === sourceAgentColIndex) {
        return 'left-start-host-target';
      }

      if (isLeftMove && columnIndex === targetAgentColIndex) {
        return 'left-end-host-target';
      }

      return `${column.property.toLowerCase()}-col-cell`;
    },
    getInfluencedByClass(row, column) {
      let influenceClass = ''
      if (!Array.isArray(row.influencedBy) || !row.influencedBy.length) {
        return influenceClass
      }
      const influenceClassArray = row.influencedBy.map((influence) =>
        ` influencedby-count_${influence.count}_${influence.influencer.replace(/[:.]/g, "_")}`
      );
      influenceClass = influenceClassArray.join('');
      return influenceClass;
    },
    selectRowByIndex(index) {
      if (_.isEmpty(this.$refs.iocTable.data[0])) {
        this.$refs.iocTable.setCurrentRow(this.iocs[++index])
      } else {
        this.$refs.iocTable.setCurrentRow(this.iocs[index])
      }
      try {
        let tableRows = this.$el.querySelectorAll('tr.el-table__row')
        tableRows[index].scrollIntoView({
          behavoir: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        })
      } catch (error) {
        console.log(`${error}`)
      }
    },
    handleRowClick(row, column, event) {
      this.$refs.iocTable.setCurrentRow(row)
      this.currentRow = row
      if (event) {
        this.currentRow.elmTarget = event.currentTarget
        this.currentRow.classList = []
        if (event.currentTarget.classList.contains('influenceby')) {
          this.currentRow.classList.push('influenceby')
        }
        if (event.currentTarget.classList.contains('current-influencer')) {
          this.currentRow.classList.push('current-influencer')
        }
      }

      if (typeof this.onRowClick === 'function') {
        this.onRowClick(row)
      }
    },
    handleHostClick(col) {
      let hostInfo = this.getHostDetails(col)
      if (typeof this.onHostClick === 'function' && hostInfo) {
        this.onHostClick(hostInfo)
      }
    },
    clearRowSelection() {
      // need gets before clearing row, else currentRow will be undefined from handleCurrentChange()
      let selElm = this.currentRow.elmTarget ? this.currentRow.elmTarget : undefined
      let classList = this.currentRow.classList ? this.currentRow.classList : []
      this.$refs.iocTable.setCurrentRow()
      this.$nextTick(() => {
        // add back css classes wiped from element ui toggling selection
        if (selElm && classList.length > 0) {
          let rowIndex = selElm.rowIndex
          try {
            let rhTableEl = document.querySelectorAll('.el-table__body-wrapper tbody tr')
            rhTableEl[rowIndex].classList.add(...classList)
          } catch (error) {
            console.log(true)
          }
        }
      })
    },
    clearSelection() {
      this.$refs.iocTable.clearSelection()
    },
    refreshLayout() {
      this.setTableRowBorderHeight()
      this.$refs.iocTable.doLayout()
    },
    setTableRowBorderHeight() {
      try {
        let elHeight = this.$el.clientHeight

        let iocTableHeight = this.$el.querySelector('.el-table__body-wrapper table').clientHeight

        // use whichever height is taller: element height or table height (case of overflow, this will be greater)
        elHeight = Math.max(elHeight, iocTableHeight)

        document.documentElement.style.setProperty('--ioc-table-tr-border-height', `${elHeight}px`)
      } catch (err) {
        console.log('Error updating attack details tr height')
      }
    },
    updateHostPath(newVal, oldVal) {
      // true after first fetch, skip iterations after
      if (newVal !== oldVal && !_.isEmpty(newVal)) {
        this.columns = _.cloneDeep(this.trail.hostPath)
        this.recordCount = this.trail.getDetectionsCount()
      }
      if (!_.isEmpty(newVal)) {
        this.trail.hostPath.forEach(col => {
          let hostInfo = this.getHostDetails(col)
          if (hostInfo) {
            if (hostInfo.customHostname !== 'UNNAMED') {
              col.altLabel = hostInfo.customHostname
            }
            col.criticality = hostInfo.criticality
            col.icon = `icon-${hostInfo.system.os.toLowerCase()}`
            col.os = hostInfo.os
          }
          // get container name
          if (!_.isEmpty(col.parentHeader)) {
            col.containerName = this.getContainerDetails(col)
          }
        })
      }
    }
  },
  computed: {
    ...mapGetters(['agentList', 'containerListInfo'])
  },
  mounted() {
    // no selection on init
    if (typeof this.onSelectionChange === 'function') {
      this.handleCurrentChange()
    }
    if (typeof this.onRowClick === 'function') {
      this.handleRowClick()
    }
    this.$refs.iocTable.setCurrentRow()
  },
  watch: {
    'trail.hostPath': {
      handler: 'updateHostPath',
      deep: false,
      immediate: true
    }
  },
  updated: function () {
    this.$nextTick(function () {
      // After render and rerender, compute and set height of tr border (gives us the dashed grid lines)
      this.setTableRowBorderHeight()
    })
  },
  beforeDestroy() {
    // On destroy, remove root variable we set for --ioc-table-tr-border-height
    document.documentElement.style.removeProperty('--ioc-table-tr-border-height')
  },
  components: {
    IocBezierCurve,
    SeverityTag
  }
}
</script>

<style>
@import 'assets/styles/_variables.css';
@import 'assets/styles/icon.css';

.ioc-trail-table {
  width: 100%;
  height: 100%;

  h5 {
    padding-left: 20px;
  }

  .table-menu-title,
  .table-menu-options {
    padding-top: 5px;
  }

  .table-menu-title {
    padding-left: 10px;
  }

  .menu-toggle {
    margin-left: 5px;
  }

  .icon-menu-toggle {
    background: var(--aluminum-900);
  }

  .icon-legend-assistant {
    background: transparent;
  }

  .el-input__inner {
    border: none;
  }

  .el-table__header-wrapper {
    border-bottom: 1px solid var(--border-color);
  }

  .el-table__header {
    table-layout: auto;
  }

  .el-table__fixed {
    background-color: inherit;
    z-index: 99;
  }

  .ioc-details {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .ioc-details_time {
      max-height: 15px;

      time,
      p {
        vertical-align: top;
      }
    }
  }

  .right-start {
    overflow: visible !important;
  }

  .el-table th.is-leaf {
    border-bottom: none;
    line-height: 20px;
    vertical-align: bottom;

    &.header-label_hide {
      text-overflow: unset;

      > .cell {
        text-overflow: unset;
      }

      .host-label {
        display: none;
      }
    }

    .host-label {
      /* width: calc(100% - 25px); */
      text-overflow: ellipsis;
      overflow: hidden;
      padding-left: 0;
    }

    > .cell {
      line-height: 20px;

      div {
        line-height: 20px;
      }
    }

    .icon {
      background: var(--gray-300);
    }
  }

  .el-table__body .cell {
    overflow: visible;
  }

  /** Dummy first row is used to create this border */

  .el-table__row:first-of-type {
    td {
      &::after {
        content: '';
        position: absolute;
        border-left: 1px dashed var(--border-color);
        left: 32px;
        top: 0px;
        height: var(--ioc-table-tr-border-height);
        z-index: 1;
      }
    }
  }

  /** Border above will be hidden behind this element */

  .hit-host-target {
    z-index: 99;
  }

  .el-table td,
  .el-table th {
    padding: 0px 0px;
  }

  .body1 {
    color: var(--aluminum-900);
  }

  .ioc-summary_details {
    position: relative;
    padding: 8px 10px 8px 6px;
    background-color: var(--denim-50);
    margin: 12px 0px;
    border: 1px solid var(--aluminum-600);
    border-bottom-left-radius: 140px;
    border-top-left-radius: 140px;
    max-width: 350px;

    p {
      margin: 0px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .ioc-details {
      margin-left: 16px;
      visibility: visible;
      transition: width 1s visibility 0.5s ease-in-out;
    }

    &.details-hidden {
      max-width: 0px;
      background-color: transparent;
      border: 0px;
      border-bottom-left-radius: 0px;
      border-top-left-radius: 0px;

      .ioc-details {
        visibility: hidden;
      }

      .ioc-occurrence {
        left: 150%;
        top: 65%;
      }
    }
  }

  .ioc-summary_details.stacked:not(.details-hidden),
  .ioc-summary_details.stacked:not(.details-hidden)::before,
  .ioc-summary_details.stacked:not(.details-hidden)::after {
    /* Add shadow to distinguish sheets from one another */
    box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.15);
  }

  .ioc-summary_details.stacked:not(.details-hidden)::before,
  .ioc-summary_details.stacked:not(.details-hidden)::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: var(--denim-50);
    border: 1px solid var(--border-color);
  }

  /* stack card of ioc-summary_details */

  .ioc-summary_details.stacked:not(.details-hidden)::before {
    left: 5px;
    top: 3px;
    z-index: -1;
    border-bottom-left-radius: 140px;
    border-top-left-radius: 140px;
  }

  /* stack another card of ioc-summary_details */

  .ioc-summary_details.stacked:not(.details-hidden)::after {
    left: 10px;
    top: 8px;
    z-index: -2;
    border-bottom-left-radius: 140px;
    border-top-left-radius: 140px;
  }

  .el-table__row.influenceby {
    background-color: var(--candlelight-50);

    &.current-influencer {
      background-color: var(--candlelight-200);
    }
  }

  .el-table__row:not(.lateral-move-row):not(.influenceby) {
    background: white;
  }

  .ioc-point {
    height: 32px;
    width: 32px;
    min-width: 32px;
    min-height: 32px;
    line-height: 34px;
    border: 1px solid var(--aluminum-600);
    border-radius: 50%;
    background-color: white;
    text-align: center;
  }

  .ioc-score {
    position: absolute;
    left: 30px;
    top: 0px;
    min-width: 100px;
  }

  .ioc-occurrence {
    position: absolute;
    left: 98%;
    top: 75%;
    font-size: 1em;
    border: 1px solid var(--border-color);
    border-radius: 50%;
    height: 25px;
    width: 25px;
    text-align: center;
    z-index: 99;
    background: var(--aluminum-300);

    p {
      vertical-align: top;
      line-height: 25px;
    }
  }

  .ioc-risklevel {
    position: relative;
    top: 5px;
    width: 8px;
    height: 8px;
    display: inline-block;
    border: 1px solid var(--aluminum-600);
    border-radius: 50%;
    vertical-align: bottom;
  }

  .icon {
    margin-bottom: 8px;
  }

  .tactic-icon,
  .icon-detections-default {
    display: inline-block;
    background: var(--aluminum-800);
  }

  .icon-detections-collection,
  .icon-detections-command_control,
  .icon-detections-command_and_control,
  .icon-detections-credential_access,
  .icon-detections-defense_evasion,
  .icon-detections-discovery,
  .icon-detections-exfiltration,
  .icon-detections-impact,
  .icon-detections-initial_access,
  .icon-detections-persistence,
  .icon-detections-lateral_movement,
  .icon-detections-privilege_escalation {
    vertical-align: text-bottom;
  }

  .icon-detections-execution {
    vertical-align: baseline;
  }

  .icon-detections-exfiltration {
    margin-left: 5px;
    margin-top: 3px;
  }

  .icon-detections-discovery,
  .icon-detections-command_control,
  .icon-detections-command_and_control,
  .icon-detections-impact,
  .icon-detections-persistence {
    margin-top: 4px;
  }

  .icon-detections-defense_evasion {
    margin-top: 5px;
  }

  .icon-detections-default .local-host-target {
    z-index: 99;
    margin-left: -2px;
  }

  .lh-40 {
    line-height: 40px;
  }

  .el-table__row.current-row {
    > td {
      background-color: var(--primary-blue-400);
    }
  }
}

#ioc-timeline-table {
  .el-table__body-wrapper {
    overflow-x: auto;
    height: calc(100% - 100px) !important;
  }

  .el-table__body {
    table-layout: auto;
    width: max-content !important;

    td {
      border-bottom: 2px solid white;
    }
  }
}

.lh-20 {
  line-height: 20px;
}
</style>

<style scoped>
>>> .el-dialog {
  min-width: 800px;
}

>>> .el-dialog__body {
  padding: 0px;
}

#progression_legend {
  width: 100%;
  min-width: 800px;
  min-height: 676px;
}

>>> .host-data {
  text-transform: capitalize;

  .host-data-row {
    display: flex;

    .field-label {
      width: 65px;
    }
  }
}

.child-host-header {
  display: flex;
  margin-top: 10px;
  align-items: center;
  padding: 0;

  .icon-subdirectory-arrow {
    margin-right: 8px;
    margin-left: 10px;
  }
}

.tooltip-wrapper {
  width: 100%;
  margin-top: 10px;
  margin-bottom: 8px;
}

.host-box-wrapper {
  max-width: 215px;
}

.host-box {
  max-width: 180px;
  width: 100%;
  border-radius: 4px;
  border: 1px solid #C2C4C7;
  background: #F4F9FF;
  display: flex;
  align-items: center;
  padding: 4px 16px;
  justify-content: center;
  cursor: pointer;

  .host-name {
    padding: 0;
  }

  .icon {
    margin: 0 auto;

    &.icon-criticality {
      margin: 0 5px 0 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/table/IOCTrailTableElement.vue